import React, { useState, useEffect } from 'react';

const BreadOrderForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    orders: [
      { breadType: '', quantity: 1, notes: '' }
    ],
    pickupDate: '',
    pickupTime: '',
    specialRequests: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const breadTypes = [
    { value: 'italian',        label: 'Italian Bread', price: 7.00 },
    { value: 'italian-pork',   label: 'Italian Bread w/Pork', price: 8.50 },
	{ value: 'italian-olives', label: 'Italian Bread w/Olives', price: 7.50 },
    { value: 'italian-rolls',  label: 'Italian Rolls', price: 6.25 },
    { value: 'stuffed-peperoni', label: 'Stuffed Bread w/Peperoni', price: 7.50 },
    { value: 'stuffed-sausage',  label: 'Stuffed Bread w/Sausage', price: 7.75 },
    { value: 'stuffed-rabe',     label: 'Stuffed Bread w/Broccoli Rabe', price: 7.25 },
    { value: 'stuffed-cheese',   label: 'Stuffed Bread w/Cheese', price: 7.00 }
  ];

  const timeSlots = [
    '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        customerName: '',
        email: '',
        phone: '',
        orders: [{ breadType: '', quantity: 1, notes: '' }],
        pickupDate: '',
        pickupTime: '',
        specialRequests: ''
      });
      setSubmitMessage('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOrderChange = (index, field, value) => {
    const newOrders = [...formData.orders];
    newOrders[index] = { ...newOrders[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      orders: newOrders
    }));
  };

  const addBreadOrder = () => {
    setFormData(prev => ({
      ...prev,
      orders: [...prev.orders, { breadType: '', quantity: 1, notes: '' }]
    }));
  };

  const removeBreadOrder = (index) => {
    if (formData.orders.length > 1) {
      const newOrders = formData.orders.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        orders: newOrders
      }));
    }
  };

  const calculateTotal = () => {
    return formData.orders.reduce((total, order) => {
      const breadType = breadTypes.find(b => b.value === order.breadType);
      return total + (breadType ? breadType.price * order.quantity : 0);
    }, 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitMessage('Order submitted successfully! We\'ll contact you to confirm your order.');
      setIsSubmitting(false);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);
    }, 1500);
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="bread-order-overlay" onClick={onClose}>
      <div className="bread-order-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="bread-order-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        {/* Modal Header */}
        <div className="bread-order-header">
          <h2>
            <i className="fas fa-bread-slice"></i>
            Fresh Bread Order
          </h2>
          <p className="bread-order-subtitle">Order your favorite Italian breads for <b>PICKUP ONLY</b></p>
        </div>

        {/* Modal Content */}
        <div className="bread-order-content">
          {submitMessage ? (
            <div className="order-success">
              <i className="fas fa-check-circle"></i>
              <h3>Thank You!</h3>
              <p>{submitMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bread-order-form">
              {/* Customer Information */}
              <div className="form-section">
                <h3><i className="fas fa-user"></i> Customer Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerName">Full Name *</label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Bread Orders */}
              <div className="form-section">
                <h3><i className="fas fa-shopping-basket"></i> Bread Selection</h3>
                {formData.orders.map((order, index) => (
                  <div key={index} className="bread-order-item">
                    <div className="order-item-header">
                      <span>Item {index + 1}</span>
                      {formData.orders.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBreadOrder(index)}
                          className="remove-item-btn"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bread Type *</label>
                        <select
                          value={order.breadType}
                          onChange={(e) => handleOrderChange(index, 'breadType', e.target.value)}
                          required
                        >
                          <option value="">Select bread type</option>
                          {breadTypes.map(bread => (
                            <option key={bread.value} value={bread.value}>
                              {bread.label} - ${bread.price}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group quantity-group">
                        <label>Quantity *</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={order.quantity}
                          onChange={(e) => handleOrderChange(index, 'quantity', parseInt(e.target.value))}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Special Notes</label>
                      <input
                        type="text"
                        placeholder="e.g., extra crispy, well-done, etc."
                        value={order.notes}
                        onChange={(e) => handleOrderChange(index, 'notes', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                
                <button type="button" onClick={addBreadOrder} className="add-item-btn">
                  <i className="fas fa-plus"></i> Add Another Bread
                </button>
              </div>

              {/* Pickup Information */}
              <div className="form-section">
                <h3><i className="fas fa-calendar-alt"></i> Pickup Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="pickupDate">Pickup Date *</label>
                    <input
                      type="date"
                      id="pickupDate"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleInputChange}
                      min={getTomorrowDate()}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pickupTime">Pickup Time *</label>
                    <select
                      id="pickupTime"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select time</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="specialRequests">Special Requests</label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    placeholder="Any special requests or dietary requirements..."
                    rows="3"
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="summary-total">
                  <strong>Total: ${calculateTotal()}</strong>
                </div>
                <p className="order-note">
                  <i className="fas fa-info-circle"></i>
                  Payment will be collected at pickup. We'll contact you to confirm your order.
                </p>
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button type="submit" className="submit-order-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Submitting Order...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Submit Order
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreadOrderForm;
